import { Maybe } from "types/common"

export type Comments = {
  id: string
  user_id: string
  commentor: string
  comment: string
  category: string
  created_at: string
}

export enum BonusType {
  DEPOSIT = "DEPOSIT",
  CUSTOM = "CUSTOM",
  PROMO = "PROMO",
  FREESPINS = "FREESPINS",
  FREEBET = "FREEBET"
}

export enum BonusEligibility {
  CASINO = "CASINO",
  SPORTS = "SPORTS",
  ALL = "ALL",
  ORIGINALS = "ORIGINALS"
}

export type Bonus = {
  id: string
  type: BonusType
  eligibility: BonusEligibility
  title: string
  bonus_percent: number
  max_usage_per_user: number
  max_bonus_amount: number
  active: boolean
  days_to_expire: number
  wager_requirements: number
  min_deposit: Maybe<number>
  zero_out_threshold: Maybe<number>
  bet_max_limit: Maybe<number>
  withdraw_max_limit: Maybe<number>
  max_cashout_amount: Maybe<number>
  created_at: Date
  updated_at: Date
}

export enum CardGateway {
  SP = "SP",
  FS = "FS",
  FV0 = "FV0",
  GB1 = "GB1",
  GB2 = "GB2",
  FV1 = "FV1",
  FV2 = "FV2"
}

/**
 * Mirrors core-api `TenantOriginalsColorsData` (core-api/src/types/admin/cms.ts),
 * which owns the storage contract. Editor rule: store real colours or no block;
 * the editor reads any object as "overrides enabled", and `withCleanOriginals`
 * (EditorForm) enforces the rule on submit.
 */
export type TenantOriginalsColorsData = {
  /** Board background. The background ladder steps off this. */
  surface?: string
  /** Accent: primary button, active tab, slider fill, glows. */
  accent?: string
  /** Primary reading text — amounts, headings, values. */
  text?: string
  /** Secondary text — field labels, hints, disabled states. */
  textMuted?: string
  /** Borders, dividers, input outlines. */
  border?: string
  /** Fill inside gradient-bordered cards. */
  card?: string
  /** Text ON the accent button — contrasts with the accent, not the board. */
  buttonText?: string
  /** Primary CTA gradient stops, >= 2 required (one stop is an invalid gradient). Repeat one colour for a flat button. */
  gradient?: string[]
}

export type TenantColorsData = {
  primary: string
  secondary: string
  success: string
  header: string
  /** See TenantOriginalsColorsData. */
  originals?: TenantOriginalsColorsData
  gradient: {
    main: string[]
    titleDividersGradient: string[]
    walletButtonGradient: string[]
    textGradient: string[]
  }
  modals: {
    main: string
    inner: string
    dialog: string
    dropdown: string
  }
  walletHoverBg: string
  walletBoxShadow: string
  viewMoreColor: string
  accordionBorderColor: string
  iconLinesColor: string
  internalNavDotDividerColor: string
  internalNavWrappingBorderColor: string
  cardBorder: string
  contentButtonTextColor: string
  outlinedButtonTextColor: string
}

export enum enumEnCurrencyFlag {
  US = "us",
  GB = "gb"
}

export type TenantContactsType = {
  community: {
    twitter: string
    discord: string
    telegram: string
    instagram: string
  }
  contact: {
    support: string
    complaints: string
    LiveChat: string
  }
}
export type CioTransactionalTemplates = {
  activationEmail?: string | number
  passwordReset?: string | number
  kycRejected?: string | number
  withdrawalSuccessful?: string | number
  passwordChanged?: string | number
  kycApproved?: string | number
  kycRequest?: string | number
  selfExclusionConfirm?: string | number
  selfExclusionSetDuration?: string | number
  selfExclusionEnded?: string | number
  adminMessage?: string | number
}

export type CioEventNames = {
  depositSuccess?: string
  withdraw?: string
  depositFailed?: string
  withdrawalRequested?: string
  birthdayToday?: string
  activationEmail?: string
  passwordResetRequested?: string
  passwordChanged?: string
  kycApproved?: string
  kycRejected?: string
  withdrawalSuccessful?: string
}

export type CioRegion = "US" | "EU"

export type CioAmountFormat = "formatted" | "raw"

export type CioTenantConfig = {
  enabled: boolean
  siteId?: string
  apiKey?: string
  appApiKey?: string
  region?: CioRegion
  amountFormat?: CioAmountFormat
  transactionalTemplates?: CioTransactionalTemplates
  eventNames?: CioEventNames
}

export const LIVE_CHAT_PROVIDERS = ["none", "intercom", "tawk", "inhouse"] as const
export type LiveChatProvider = (typeof LIVE_CHAT_PROVIDERS)[number]

export type LiveChatPlacement = {
  desktopOnly?: boolean
  hideOnSportsbook?: boolean
  position?: "br" | "bl"
  horizontalPadding?: number
  sportsbookHorizontalPadding?: number
  verticalPadding?: number
}

export type LiveChatConfig = {
  provider?: LiveChatProvider
  placement?: LiveChatPlacement
  intercom?: { appId?: string; hashKey?: string }
  tawk?: { propertyId?: string; widgetId?: string; apiKey?: string }
  inhouse?: { host?: string; publicKey?: string; hashSecret?: string }
}

type NullableFields<T> = { [K in keyof T]?: T[K] | null }

/**
 * RFC 7396: an absent key keeps the stored value, an explicit null clears it.
 * Null rather than undefined because `JSON.stringify` drops undefined keys,
 * so a cleared field would never reach the server.
 */
export type LiveChatConfigPatch = {
  provider?: LiveChatProvider | null
  placement?: NullableFields<LiveChatPlacement>
  intercom?: NullableFields<NonNullable<LiveChatConfig["intercom"]>>
  tawk?: NullableFields<NonNullable<LiveChatConfig["tawk"]>>
  inhouse?: NullableFields<NonNullable<LiveChatConfig["inhouse"]>>
}

/** GET /admin/settings/live-chat: secrets masked, plus what is stored and what env provides. */
export type LiveChatSettings = LiveChatConfig & {
  secretsPresent: { intercomHashKey: boolean; tawkApiKey: boolean; inhouseHashSecret: boolean }
  envFallbacks: { intercomHashKey: boolean; tawkApiKey: boolean }
}

export type CioApiCheckResult = { ok: true } | { ok: false; error: string }

export type CioConnectionStatus = {
  configured: boolean
  credentialsSource: "db" | "env" | "mixed" | "none"
  appApi: CioApiCheckResult
  trackApi: CioApiCheckResult
}

export const GAME_CARD_MODES = ["dynamic", "static"] as const
export type GameCardMode = (typeof GAME_CARD_MODES)[number]
export type GameCardConfig = {
  enabled?: boolean
  mode?: GameCardMode
  range?: {
    min?: number
    max?: number
  }
}

export type FontConfig = {
  fontFamily: string
  fontUrl: string
}

export type FakeBetPlayer = {
  username: string
  display_name: string
  avatar_url?: string
  tierId?: string
}

export const FAKE_BETS_NAME_POOL_MODES = ["DEFAULT_POOL", "CUSTOM_POOL"] as const
export type FakeBetsNamePoolMode = (typeof FAKE_BETS_NAME_POOL_MODES)[number]

export const FAKE_BETS_PLAYER_MODES = ["incognito", "namePool", "random"] as const
export type FakeBetsPlayerMode = (typeof FAKE_BETS_PLAYER_MODES)[number]

export const FAKE_BETS_GAME_SELECTION_MODES = ["random", "specific", "provider"] as const
export type FakeBetsGameSelectionMode = (typeof FAKE_BETS_GAME_SELECTION_MODES)[number]

export const FAKE_BETS_STRATEGIES = ["boost", "fill"] as const
export type FakeBetsStrategy = (typeof FAKE_BETS_STRATEGIES)[number]

export const FAKE_BETS_MAX_VOLUME_PER_HOUR = 2000

/** Mirrors core-api's `FakeBetsConfig` (src/types/admin/cms.ts) — keep both in sync. */
export type FakeBetsConfig = {
  enabled: boolean
  volumePerHour: number
  strategy: FakeBetsStrategy
  playerMode: FakeBetsPlayerMode
  namePool: { mode: FakeBetsNamePoolMode; entries?: FakeBetPlayer[] }
  gameSelection: { mode: FakeBetsGameSelectionMode; gameIds?: string[]; providerIds?: string[] }
  allowedCoins: string[]
  betAmount: { min: number; max: number }
  winRate: { probability: number; multiplierMin: number; multiplierMax: number }
  highRollerBoost: { probability: number }
}

/** A configured license/certification seal (e.g. `{ type: "anj", id: "5c54..." }`). */
export type TenantSeal = {
  type: string
  id: string
}

export type TenantConfigData = {
  appMobileLogo: string | File
  spinner: string | File
  Age18Icon: string | File
  mobileLogoToUp: boolean
  appDesktopLogo: string | File
  sealANJText: string
  noSealText: string
  seals: TenantSeal[]
  contacts: TenantContactsType
  enCurrencyFlag: enumEnCurrencyFlag
  isDisableFiat: boolean
  isDisableCrypto: boolean
  isPhoneVerificationEnabled: boolean
  /** CIO config lives at top-level of setup_data (not inside emailDelivery) */
  customerio?: CioTenantConfig
  liveChat?: LiveChatConfig
  emailDelivery?: {
    provider: "nodemailer" | "customerio"
  }
  gameCardConfig?: GameCardConfig
  fontConfig?: FontConfig
}

export type Tenant = {
  id: string
  color_theme: TenantColorsData
  prod_frontend: string
  stage_frontend: string
  setup_data: TenantConfigData
}
